import React, { useCallback, useState, useEffect } from 'react';
import { fetchWithAuth, getUserInfoById } from '../services/api';
import PersonalForm from './PersonalForm';
import BattenburgLoader from '../common/BattenburgLoader';
import {
  useIsAuthorized,
  useLoadingArr,
  useVisaAktuell,
  usePersonalId,
  useSelectedPersonalFile,
  useStation,
  usePersonChanged,
  useImpersonatedUserId,
  useUserInfo
} from '../contexts/GlobalStateContext';
import { toast } from 'react-toastify';

const RollerOchPersonal = () => {
  const { impersonatedUserId } = useImpersonatedUserId();
  const { isAuthorized } = useIsAuthorized();
  const { loadingArr, setLoadingArr } = useLoadingArr();
  const { visaAktuell } = useVisaAktuell();
  const { personalId, setPersonalId } = usePersonalId("");
  const { selectedPersonalFile, setSelectedPersonalFile } = useSelectedPersonalFile();
  const [personalIdError, setPersonalIdError] = useState(false);
  const [documents, setDocuments] = useState({ personal: [""], passtider: [""], heroma: [""] });
  const [stationer, setStationer] = useState([]);
  const { station, setStation } = useStation();
  const { setPersonChanged } = usePersonChanged();
  const { userInfo } = useUserInfo();
  const [impersonatedUser, setImpersonatedUser] = useState(null);

  const loadingKeys = ['import','export'];
  const anyLoading = () => loadingKeys.some(key => loadingArr[key]);

  const isSystemAdmin = userInfo?.role === 'SystemAdmin';

  const fetchDocuments = async (station) => {
    try {
      let url = `/api/documents/list?station=${station}`;
      const response = await fetchWithAuth(url);

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Kunde inte hämta filer');
      }

      const data = await response.json();
      setDocuments({
        personal: data.personal || [],
        passtider: data.passtider || [],
        heroma: data.heroma || [],
      });
    } catch (error) {
      const msg = error.message || '❌ Kunde inte hämta filer.';
      toast.error(msg);
    }
  }

  const getStationer = async (userId) => {
    try {
      if (!userId) return;
      const url = `/api/stationer/names?userId=${userId}`;
      const response = await fetchWithAuth(url);
      if (!response.ok) throw new Error('Kunde inte hämta stationer');
      const data = await response.json();
      setStationer(data.length === 0 ? [] : data);
    } catch (error) {
      console.error(error);
    }
  }

  useEffect(() => {
    const loadData = async () => {
      if (!isAuthorized) return;
      const userId = impersonatedUserId || userInfo.userId;
      await getStationer(userId);

      if (station && station !== '---') {
        await fetchDocuments(station);

        // Om SystemAdmin, hämta detaljerad användarinformation
        if (isSystemAdmin && impersonatedUserId) {
          try {
            const data = await getUserInfoById(impersonatedUserId);
            setImpersonatedUser(data);
          } catch (error) {
            console.error('Fel vid hämtning av användardata:', error);
          }
        }
      } else {
        setSelectedPersonalFile('');
        setPersonalId('')
      }
    };

    loadData();
  }, [station, impersonatedUserId, isAuthorized, isSystemAdmin, userInfo?.userId]);

  const exporteraPersonalAsync = useCallback(async (key) => {
    setLoadingArr(prev => ({ ...prev, [key]: true }));

    try {
      let urlLink = '/api/personal/personal';
      const response = await fetchWithAuth(urlLink, {
        method: 'POST',
        headers: {
          'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        },
        body: JSON.stringify({ aktuell: visaAktuell, station: station })
      });

      if (!response.ok) {
        throw new Error('Fel vid nedladdning av personalrapport');
      }

      // Hämta filnamnet från Content-Disposition header
      let fileName = 'Personal.xlsx'; // Fallback-namn

      const contentDisposition = response.headers.get('content-disposition');
      if (contentDisposition) {
        // Extrahera filnamnet från headern
        const fileNameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (fileNameMatch && fileNameMatch[1]) {
          fileName = fileNameMatch[1].replace(/['"]/g, '');
        }
      }

      // Skapa blob och ladda ner
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      a.click();

      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (error) {
      console.error('Fel vid nedladdning av personalrapport:', error);
      throw error;
    } finally {
      setLoadingArr(prev => ({ ...prev, [key]: false }));
    }
  }, [visaAktuell, setLoadingArr]);


  const importPersonalista = async (key) => {
    if (personalId) {
      setLoadingArr(prev => ({ ...prev, [key]: true }));

      try {
        let url = '/api/personal/personallista';

        const response = await fetchWithAuth(url, {
          method: 'POST',
          body: JSON.stringify({
            documentId: personalId,
            station: station
          })
        });

        if (!response.ok) {
          const errorMsg = await response.text();
          throw new Error(errorMsg || 'Fel vid uppladdning av Excel-fil.');
        }

        await fetchDocuments(station);
        setPersonChanged(prev => !prev);
        setPersonalIdError(false);
        toast.success("Personallista inläst.");
      } catch (error) {
        setPersonalIdError(true);
        const msg = error.message || "Fel vid uppladdning av Excel-fil.";
        toast.error(msg);
      } finally {
        setLoadingArr(prev => ({ ...prev, [key]: false }));
      }
    } else {
      const msg = "Ingen fil vald.";
      toast.error(msg);
      setPersonalIdError(true);
    }
  }
  return (
    <div className="tw-px-4 sm:tw-px-6 lg:tw-px-8">
      <div className="tw-max-w-[2210px] tw-mx-auto tw-py-3">
        <div className="tw-mb-2 tw-w-full">
          {isAuthorized ? (
            <div className="tw-rounded-3xl tw-overflow-hidden tw-border tw-border-blue-500 tw-w-[2210px] tw-max-w-full tw-pb-3">
              <div className="tw-bg-gradient-to-r tw-from-blue-500 tw-to-blue-600 tw-text-white tw-px-4">
                <h3 className="tw-m-0 tw-text-2xl tw-font-bold tw-text-center">Koppla personal till roll och kompetenser</h3>
              </div>
              <div className="tw-flex tw-items-center tw-gap-4">
                <div className="tw-min-h-[40px]">
                  {anyLoading() && <BattenburgLoader left={'44%'} />}
                </div>
                <div className="tw-min-h-[40px]">
                  {impersonatedUserId && impersonatedUser && (
                    <div className="tw-mb-2">
                      <h3 className="tw-font-semibold tw-text-lg tw-text-gray-800 tw-text-center">
                        Du agerar som användare:
                        <span className="tw-text-red-500 hover:tw-text-red-700 tw-ml-1">
                          <strong>{impersonatedUser.userName}</strong>
                        </span>
                      </h3>
                    </div>
                  )}
                </div>
              </div>
              <div className="tw-flex tw-items-center tw-pb-5 tw-min-h-[40px]">
                <label htmlFor="stationer-select" className="tw-px-2 tw-ml-3 tw-text-sm tw-font-bold">
                  Station:
                </label>
                {stationer.length > 0 && (
                  <select
                    id="stationer-select"
                    value={station}
                    disabled={!isAuthorized || anyLoading()}
                    onChange={(e) => setStation(e.target.value)}
                    className="tw-min-w-[160px] tw-border tw-p-2 tw-mr-2 tw-text-sm tw-leading-normal tw-font-bold"
                  >
                    {station === '---' && <option key='placeholder_station' value="">Välj station</option>}
                    {stationer.map((table) => (
                      <option key={table} value={table}>{table}</option>
                    ))}
                  </select>
                )}
              </div>
              <div className="tw-flex tw-items-start tw-gap-3 tw-pb-3 tw-px-6">
                {/* Select */}
                <select
                  value={personalId}
                  onChange={(e) => {
                    const selectedId = e.target.value;
                    setPersonalId(selectedId);
                    setPersonalIdError(false);
                    setSelectedPersonalFile(documents.personal.find(f => f.id === parseInt(selectedId))?.name);
                  }}
                  disabled={!isAuthorized || anyLoading()}
                  className="tw-min-w-[400px] tw-h-[42px] tw-border tw-px-2 tw-rounded tw-font-bold tw-text-sm"
                >
                  {!isAuthorized ? (
                    <option key='placeholder_0' value="">Inloggning krävs</option>
                  ) : (
                    <>
                      <option key='placeholder_1' value="">Välj fil</option>
                      {documents.personal.map((file) => (
                        <option key={file.id} value={file.id}>
                          {file.name}
                        </option>
                      ))}
                    </>
                  )}
                </select>

                {/* Läs in-knapp */}
                <div className="tw-flex tw-flex-col tw-pl-7">
                  <button
                    onClick={() => {
                      importPersonalista('import', impersonatedUserId);
                    }}
                    disabled={station === '---' || loadingArr['import'] || !personalId}
                    className={
                      `tw-w-[450px] tw-h-[42px] tw-rounded tw-text-sm tw-font-bold ` +
                      (personalIdError
                        ? `tw-bg-red-600 tw-text-white ${!anyLoading() ? 'hover:tw-bg-red-700' : ''} tw-shadow-[0_0_6px_#dc2626]`
                        : loadingArr['import']
                          ? 'tw-bg-yellow-300 tw-text-black tw-shadow-[0_0_6px_#fde047]'
                          : (

                            (station === '---' ? 'tw-bg-gray-300 tw-text-gray-500 tw-cursor-not-allowed'
                              : 'tw-bg-yellow-300 tw-text-blue') +
                            (station !== '---' && !anyLoading()
                              ? ' hover:tw-bg-yellow-500'
                              : '')

                          )
                      )
                    }
                  >
                    {loadingArr['import'] ? 'Läser in...' : 'Läs in Personallista'}
                  </button>
                  <p className="tw-text-left tw-font-bold">Senast använd: {selectedPersonalFile}</p>
                </div>
                {/* Exportera-knapp */}
                <button
                  onClick={async () => { await exporteraPersonalAsync('export'); }}
                  disabled={station === '---' || loadingArr['export']}
                  className={`tw-w-[450px] tw-h-[42px] tw-rounded tw-text-sm tw-font-bold tw-shadow-[0_0_6px_#fde047]
                                ${station === '---' ? 'tw-bg-gray-300 tw-text-gray-500 tw-cursor-not-allowed'
                      : 'tw-bg-yellow-300 tw-text-blue'
                    }
                                ${station !== '---' && !anyLoading()
                      ? 'hover:tw-bg-yellow-500'
                      : ''
                    }`
                  }
                >
                  {loadingArr['export']
                    ? (visaAktuell ? 'Exporterar aktuell...' : 'Exporterar all personal...')
                    : (visaAktuell ? 'Exportera personal senaste heromaimport' : 'Exportera all personal')}
                </button>
              </div>

              <div className="tw-flex tw-items-center tw-gap-4">
                <PersonalForm />
              </div>
            </div>
          ) : (
            <div className="tw-rounded-3xl tw-overflow-hidden tw-border tw-border-blue-500 tw-w-[2210px] tw-max-w-full">
              <div className="tw-bg-gradient-to-r tw-from-blue-500 tw-to-blue-600 tw-text-white tw-px-4 tw-py-3">
                <h3 className="tw-m-0 tw-text-2xl tw-font-bold tw-text-center">Logga in för att se personal</h3>
              </div>
              <div className="tw-p-0"></div>
            </div>
          )}
        </div>
      </div>
    </div >
  );
}

export default RollerOchPersonal;